import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, ModalContentContainer } from '@/components';
import { ComplianceTypes, Modals, Routes } from '@/constants';
import { Link } from '@/containers/Link';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  selectActiveModalParams,
  selectUserCompliance,
  uiActions,
} from '@/store';
import { SnackbarUtils } from '@/utils';

export const KeepGoingPromoModalContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { promoCode, percentOff } = useSelector(selectActiveModalParams) || {};

  const compliance = useSelector(selectUserCompliance);

  const onCodeCopy = () => {
    navigator.clipboard.writeText(promoCode);
    SnackbarUtils.success(`Promo code ${promoCode} copied to clipboard!`);
  };

  const onClose = () => {
    dispatch(uiActions.closeActiveModal());
  };

  const onPricing = () => {
    AnalyticsService.pageViewPricing({
      location: {
        component: EVENT_CONSTANTS.COMPONENT.MODAL,
        modalName: Modals.KEEP_GOING_PROMO,
      },
    });
    dispatch(uiActions.closeActiveModal());
    navigate(Routes.PRICING);
  };

  return (
    <Box
      sx={{
        width: {
          xs: 'auto',
          sm: 520,
          md: 620,
        },
      }}>
      <ModalContentContainer stretch onModalClose={onClose}>
        <Typography variant="h1" color="secondary.main">
          Get {percentOff}% off your first year!
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            mb: 4,
          }}>
          You&apos;re on a roll! Finish your {compliance}{' '}
          {compliance === ComplianceTypes.CPD ? 'hours' : 'credits'} without
          interruption.
          <br /> Use this{' '}
          <span style={{ textDecoration: 'underline' }}>limited-time</span> code
          to keep going.
        </Typography>
        <Box sx={styles.rowContainer}>
          <Typography variant="h4" sx={{ flex: 1 }}>
            1. Copy coupon code:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              borderRadius: 2,
              backgroundColor: 'grey50',
              py: 1.5,
              px: 2,
              flex: 1,
            }}>
            <Box
              sx={{
                px: 1.5,
                height: 32,
                lineHeight: '100%',
                borderRadius: 2,
                backgroundColor: 'white',
                border: '2px dashed',
                borderColor: 'grey200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                textWrap: 'nowrap',
              }}>
              <Typography variant="body2SemiBold">{promoCode}</Typography>
            </Box>
            <Button fullWidth={false} label="Copy" onClick={onCodeCopy} />
          </Box>
        </Box>
        <Box sx={styles.rowContainer}>
          <Typography sx={{ flex: 1 }} variant="h4">
            2. Use code at checkout:
          </Typography>
          <Button
            fullWidth={false}
            skinny={false}
            sx={{ flex: 1 }}
            label="Unlock Full Access"
            onClick={onPricing}
          />
        </Box>
        <Box sx={{ mt: 4 }}>
          <Link
            color="text.light"
            textVariant="body2SemiBold"
            onClick={onClose}>
            Not now
          </Link>
        </Box>
      </ModalContentContainer>
    </Box>
  );
};

const styles = {
  rowContainer: {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    alignItems: {
      xs: 'flex-start',
      sm: 'center',
    },
    justifyContent: 'space-between',
    mb: 2,
    gap: 2,
  },
};
